<template>
  <div class="do-hours">
    <label class="d-label marg-1-top" v-if="isShowOptions">{{ i18n['period_hours'] }}</label>
    <DoDatePickerRange
      v-if="isShowOptions"
      :date="date"
      :startDate="startDate"
      :endDate="endDate"
      @datesChanged="setDates">
    </DoDatePickerRange>
    <Loader v-if="isLoading" :is-small="true" bg-color="#ffffff"></Loader>
    <template v-else>
      <div class="do-card--overflow" ref="dropdown" :style="{'maxHeight': maxHeight}">
        <h3 v-if="hoursGrouped.length < 1" class="text-explainer marg-1-top">{{ i18n['no_schedule'] }}</h3>
        <ul v-for="parentCat in hoursGroupedSorted" class="do-card" v-else>
          <li>
            <div class="do-hours__item do-hours__item__headline"
                 :class="{'do-hours__item__headline--no-border': parentCat.subCategories.length > 1}">
              <p>{{ parentCat.vTitle }}</p>
              <p>{{ parentCat.vHours }}</p>
            </div>
            <ul>
              <li
                v-for="subCat in parentCat.subCategories">
                <div class="do-hours__item do-hours__item__sub__headline do-hours__item__child"
                     v-if="parentCat.subCategories.length > 1">
                  <p>{{ subCat.vTitle }}</p>
                  <p>{{ subCat.vHours }}</p>
                </div>
                <ul>
                  <li v-for="subject in subCat.subjects" class="do-hours__item do-hours__item__child">
                    <p>{{ subject.vTitle }}</p>
                    <p>{{ subject.vHours }}</p>
                  </li>
                </ul>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>

<script>
import docendo from '../../docendo'
import moment from 'moment'
import Loader from '../common/Loader'
import DoDatePickerRange from '../datepicker/DoDatePickerRange'

const I18N = {
  da: {
    flextime: 'Flextid',
    flexin: 'Flex ind',
    flexout: 'Flex ud',
    teaching: 'Undervisning',
    preparation: 'Forberedelse',
    additional_tasks: 'Øvrige Opgaver',
    available: 'Rådighedstimer',
    club: 'SFO tid',
    pool: 'Lokalpulje',
    absence: 'Fravær',
    other: 'Opgaver',
    subject: 'Fag',
    person_resource: 'Ressource',
    person_resource_visible: 'Synlig ressource',
    with_kids: 'Med børn',
    without_kids: 'Uden børn',
    unknown: 'Rådighedstime',
    break: 'Pause',
    period_hours: 'Timer i perioden',
    no_schedule: 'Intet skemalagt i perioden',
    hour: 'time',
    hours: 'timer',
    min: 'min',
    optional: 'Valgfag',
    resource: 'Ressource'
  },
  en: {
    teaching: 'Teaching',
    preparation: 'Preparation',
    additional_tasks: 'Additional Tasks',
    available: 'Available Hours',
    club: 'Club Time',
    pool: 'Local Pool',
    absence: 'Absence',
    period_hours: 'Hours in the Period',
    no_schedule: 'No Schedule in the Period',
    other: 'Tasks',
    subject: 'Course',
    person_resource: 'Resource',
    person_resource_visible: 'Visible resource',
    with_kids: 'With children',
    without_kids: 'Without children',
    unknown: 'Free period',
    break: 'Break',
    hour: 'hour',
    hours: 'hours',
    min: 'min',
    optional: 'Optional',
    resource: 'Resource'
  },
  nb: {
    teaching: 'Undervisning',
    preparation: 'Forberedelse',
    additional_tasks: 'Ekstra oppgaver',
    available: 'Tilgjengelige timer',
    club: 'SFO tid',
    pool: 'Lokalpulje',
    absence: 'Fravær',
    period_hours: 'Timer i perioden',
    no_schedule: 'Ingen timeplan i perioden',
    other: 'Oppgaver',
    subject: 'Fag',
    person_resource: 'Ressurs',
    person_resource_visible: 'Synlig ressurs',
    with_kids: 'Med barn',
    without_kids: 'Uten barn',
    unknown: 'Ledig time',
    break: 'Pause',
    hour: 'time',
    hours: 'timer',
    min: 'min',
    optional: 'Valgfag',
    resource: 'Ressurs'
  }
}

export default {
  components: {Loader, DoDatePickerRange},
  props: {
    schoolId: {
      default: 7272
    },
    calendarId: {
      default: 7
    },
    date: {
      default: moment()
    },
    isShowOptions: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      startDate: moment(this.date).startOf('isoWeek'),
      endDate: moment(this.date).endOf('isoWeek'),
      hours: [],
      isLoading: false,
      maxHeight: '50vh'
    }
  },
  computed: {
    locale () {
      return this.$store.getters.locale
    },
    i18n () {
      return I18N[this.locale]
    },
    hoursGrouped () {
      // let cat = {subject: { "category": "subject", "minutes": 180, "subCategories": [ [ "subject", "KRLE", 45 ], [ "subject", "Solsort Fag", 135 ] ] }}
      let categories = []
      this.hours.forEach(subarray => {
        const masterType = subarray[0] ? subarray[0] : null
        const subjectType = subarray[1] ? subarray[1] : null
        const subjectTitle = subarray[2] ? subarray[2] : null
        const minutes = subarray[3] ? subarray[3] : 0
        const vHours = this.getMinutesToHours(minutes)
        if (masterType) {
          const masterTypeIdx = categories.findIndex(cat => cat.masterType === masterType)
          if (masterTypeIdx < 0) {
            const masterTypeObj = {
              masterType: masterType,
              vTitle: this.getTitle(masterType),
              vOrder: this.getOrder(masterType),
              vHours,
              subCategories: []
            }
            categories.push(masterTypeObj)
          } else {
            if (subjectType && !subjectTitle) {
              const subjectTypeIdx = categories[masterTypeIdx]['subCategories'].findIndex(subCat => subCat.subjectType === subjectType)
              const subjectTypeObj = {
                masterType,
                subjectType,
                vTitle: this.getTitle(subjectType),
                vHours,
                vOrder: this.getOrder(subjectType),
                subjects: []
              }
              if (subjectTypeIdx < 0) {
                categories[masterTypeIdx]['subCategories'].push(subjectTypeObj)
              } else {
                categories[masterTypeIdx]['subCategories'][subjectTypeIdx].push(subjectTypeObj)
              }
            }
            if (subjectType && subjectTitle) {
              const subjectTypeIdx = categories[masterTypeIdx]['subCategories'].findIndex(subCat => subCat.subjectType === subjectType)
              const subjectIdx = categories[masterTypeIdx]['subCategories'][subjectTypeIdx]['subjects'].findIndex(subject => subject.subjectTitle === subjectTitle)
              const subjectObj = {
                masterType,
                subjectType,
                subjectTitle,
                vTitle: subjectTitle,
                vHours
              }
              if (subjectIdx < 0) {
                categories[masterTypeIdx]['subCategories'][subjectTypeIdx]['subjects'].push(subjectObj)
              } else {
                categories[masterTypeIdx]['subCategories'][subjectTypeIdx]['subjects'][subjectIdx].push(subjectObj)
              }
            }
          }
        }
      })
      return categories
    },
    hoursGroupedSorted () {
      // Create a copy of the original array to avoid modifying it directly
      const sortedHoursGrouped = [...this.hoursGrouped]
      // Sort the copied array based on the 'vOrder' property
      sortedHoursGrouped.sort((a, b) => a.vOrder - b.vOrder)
      // Sort the subCategories within each item
      sortedHoursGrouped.forEach(item => {
        if (item.subCategories) {
          item.subCategories.sort((a, b) => a.vOrder - b.vOrder)
        }
      })
      return sortedHoursGrouped
    }
  },
  methods: {
    getTitle (label) {
      if (this.i18n[label]) {
        return this.i18n[label]
      } else {
        return label
      }
    },
    getOrder (label) {
      const orders = {
        flextime: 1,
        teaching: 1,
        subject: 1,
        person_resource: 3,
        person_resource_visible: 2,
        with_kids: 1,
        without_kids: 2,
        additional_tasks: 2,
        other: 3,
        preparation: 4,
        available: 7,
        club: 6,
        pool: 5,
        unknown: 7,
        break: 2,
        absence: 12
      }
      return orders[label] ? orders[label] : 20
    },
    setDates (startAndEnd) {
      const start = startAndEnd.start
      const end = startAndEnd.end
      if (start) {
        this.startDate = start
      }
      if (end) {
        this.endDate = end
      }
      this.readHours()
    },
    readHours () {
      this.isLoading = true
      this.hours = []
      const start = moment(this.startDate).format('YYYY-MM-DD')
      const end = moment(this.endDate).format('YYYY-MM-DD')
      docendo.hoursCountForCalendar(this.schoolId, this.calendarId, start, end, success => {
        this.hours = JSON.parse(success)
        this.isLoading = false
      }, failure => {
        console.log({failure})
      })
    },
    getMinutesToHours (minutes) {
      const totalMinutes = Math.abs(minutes).toFixed(0) // Absolute value for calculation
      const hours = Math.floor(totalMinutes / 60)
      const remainingMinutes = totalMinutes % 60
      const hoursLabel = hours !== 1 ? this.i18n['hours'] : this.i18n['hour']
      // Determine the sign of the original minutes
      const sign = minutes < 0 ? '-' : ''
      // Format the output
      const hoursAndHoursLabel = hours > 0 ? hours + ' ' + hoursLabel + ' ' : ''
      return sign + hoursAndHoursLabel + remainingMinutes + ' ' + this.i18n['min']
    },
    calculateMaxHeight () {
      const pxFromTopOfWindow = this.$refs.dropdown.getBoundingClientRect().top
      const maxHeight = window.innerHeight - (pxFromTopOfWindow + 30)
      this.maxHeight = `${maxHeight}px`
    }
  },
  mounted () {
    this.calculateMaxHeight()
    window.addEventListener('resize', this.calculateMaxHeight)
    this.readHours()
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.calculateMaxHeight)
  }
}
</script>

<style scoped>
.do-hours {
  width: fit-content;
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  gap: 0.5rem;
  padding-bottom: 1rem;
}

.do-hours ul {
  margin: 0;
}

.do-hours__item {
  display: flex;
  justify-content: space-between;
  border: 1px solid #cbd6e0;
  border-width: 0 1px;
}

.do-hours__item:not(.do-hours__item__headline) {
  color: #7a818c;
}

.do-hours__item__headline {
  font-weight: bold;
  background: #f6f9fc;
  border: 1px solid #cbd6e0;
  padding: 0.5rem;
  border-radius: 4px 4px 0 0;
}

.do-hours__item__child {
  padding: 0.15rem 0.5rem 0.15rem 1.5rem;
}

.do-card {
  border-bottom: 1px solid #cbd6e0;
  border-radius: 4px;
}

.do-hours__item__sub__headline {
  background: #f9fafb;
  border: 1px solid #cbd6e0;
  font-weight: bold;
  padding-left: 1rem;
}

.do-hours__item__headline--no-border {
  border-bottom: 0;
}

.do-card--overflow {
  max-height: 70vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  overscroll-behavior: contain;
}

</style>



// WEBPACK FOOTER //
// CalculateHours.vue?6dc705f0